import React from "react";
import { FLAG_AXIS } from "../../../../../../../../Chart/series/getAnalysisFlagSerie";
import validateInterface from "@ui/lib/validateInterface";
import { FailureMetadata, type Status } from "../../../../../../../../Chart/PointMetadata";
import type { Options } from "@ui/Highcharts";
import useYMinMax from "@ui/Highcharts/hooks/useYMinMax";
import t from "../../../../../../../../../../i18n";
import useTooltipClick from "@ui/Highcharts/hooks/useTooltipClick";
import useNoDataPlotBands from "@ui/Highcharts/hooks/useNoDataPlotBands";
import { useDiagnosticWithQuantityNoDataIntervals } from "../../../../../../../../useNoDataIntervals";
import { useFailureValuesMutation } from "../../../../../../../../Diagnosis/useFailureStatusMutation";
import { useTranslations } from "../../../../../../../../../../api/useTranslations";
import { useSettings } from "../../../../../../../../../../api/useSettings";
import useGroupBreaks from "../../../../../../../../Chart/hooks/useGroupBreaks";
import { FAULT_AMPLITUDE_CHART } from "./useFaultGraphOptions";
import type {
  PointOptionsObject,
  SeriesAreaOptions,
  SeriesArearangeOptions,
  SeriesLineOptions,
  SeriesScatterOptions,
} from "highcharts";
import {
  failureEvolutionConstantTooltipFormatter,
  failureEvolutionTooltipClickHandler,
  type FailureEvolutionHookOptions,
} from "../../../../../../../../Diagnosis/FailureEvolution/useOptions";
import { isFocusedFaultSeries } from "../../isFocusedFaultSeries";
import useAnalysisXAxis from "../../../../../../../hooks/useAnalysisXAxis";
import { y_axis } from "@ui/Chart/Axis";
import type { Event } from "../../../../../../../../../../api/useEvents";
import { getEventVisibleSeries } from "../../../../../../../utils/event";
import { CHART_SELECTION_PLOT_LINE_MARGIN } from "../../../../../../../../Chart/hooks/useAnalysisSelectionPlotLines";

interface P extends FailureEvolutionHookOptions {
  failureNames: string[];
  chartTypeOption: { label: string; value: string };
  event: Event;
}

const useOptions = ({
  source,
  project,
  failureNames,
  yAxisId,
  chartRef,
  analyses,
  indexedFailureValues,
  series,
  chartTypeOption,
  qoi,
  event,
}: P) => {
  const settings = useSettings();
  const translations = useTranslations();
  const xBreaks = useGroupBreaks(project.id, analyses);
  const initialXAxisOptions = useAnalysisXAxis({
    chartRef,
    project,
    source,
    analyses,
    event,
    boundaries: "active-analyses",
  });

  const initialYAxisOptions = useYMinMax({
    chartRef,
    yAxisId,
    scope: `${qoi ? `sign-${-1}` : "qoi"}`,
  });

  useNoDataPlotBands({
    chartRef,
    intervals: useDiagnosticWithQuantityNoDataIntervals(analyses, indexedFailureValues),
  });

  const chartTypeOptionLabelRef = React.useRef(chartTypeOption.label);
  const qoiRef = React.useRef(qoi);
  const mutateFailureValues = useFailureValuesMutation(project);

  chartTypeOptionLabelRef.current =
    chartTypeOption.value === FAULT_AMPLITUDE_CHART
      ? t("diagnosis.overview.amplitude")
      : chartTypeOption.label;

  qoiRef.current = qoi;

  useTooltipClick(
    chartRef,
    React.useCallback(
      (action: Status, points: Highcharts.Point[]) =>
        failureEvolutionTooltipClickHandler(action, points, mutateFailureValues),
      [mutateFailureValues],
    ),
  );

  const constantTooltipFormatter = React.useCallback(
    function fn(this: Highcharts.TooltipFormatterContextObject) {
      return failureEvolutionConstantTooltipFormatter(
        this,
        qoiRef,
        project,
        settings,
        translations,
      );
    },
    [translations, project, settings],
  );

  const findMagnitudeFaultUnit = React.useCallback(
    (
      series: (
        | SeriesArearangeOptions
        | SeriesAreaOptions
        | SeriesLineOptions
        | SeriesScatterOptions
      )[],
    ) => {
      const { included, excluded } = getEventVisibleSeries(event);
      const findFaultSerie = series.find(
        serie =>
          (serie.id ? isFocusedFaultSeries(serie.id, included, excluded, failureNames) : false) &&
          String(serie.yAxis).endsWith(FAULT_AMPLITUDE_CHART),
      );
      const datas = findFaultSerie ? (findFaultSerie.data ?? []) : [];
      const unit = datas
        .map(d => validateInterface(FailureMetadata, (d as PointOptionsObject)?.custom))
        .map(d => d.human?.unit)
        .filter(Boolean)
        .at(-1);

      return unit ?? "";
    },
    [failureNames, event],
  );
  const isMagnitudeChartType = chartTypeOption.value === FAULT_AMPLITUDE_CHART;

  return React.useMemo(() => {
    const _: Options = {
      initial: {
        xAxis: initialXAxisOptions,
        yAxis: [initialYAxisOptions],
      },
      chart: {
        zoomType: "xy",
        marginTop: CHART_SELECTION_PLOT_LINE_MARGIN,
      },
      series,
      xAxis: {
        breaks: xBreaks,
      },
      yAxis: [
        {
          id: yAxisId,
          visible: true,
          title: {
            useHTML: true,
            text: isMagnitudeChartType
              ? t(
                  "project.fault_event.chart.magnitude.axis.title",
                  failureNames.join(", "),
                  findMagnitudeFaultUnit(series),
                )
              : qoi
                ? t("diagnosis.chart.evolution.axis.y", qoi.name, qoi.unit)
                : "",
            offset: y_axis.title.offset,
            style: {
              color: y_axis.title.color,
              font: y_axis.title.font,
            },
          },
          labels: {
            sign: false,
            enabled: true,
            x: -1 * y_axis.label.offset,
            style: {
              color: y_axis.label.color,
              font: y_axis.label.font,
            },
            useHTML: true,
          },
        },
        FLAG_AXIS,
      ],
      tooltip: {
        sticky: true,
        formatter: constantTooltipFormatter,
      },
      navigator: {
        enabled: true,
        xAxis: {
          breaks: xBreaks,
          max: Date.now(),
        },
      },
    };
    return _;
  }, [
    initialXAxisOptions,
    initialYAxisOptions,
    series,
    xBreaks,
    yAxisId,
    isMagnitudeChartType,
    failureNames,
    findMagnitudeFaultUnit,
    qoi,
    constantTooltipFormatter,
  ]);
};

export default useOptions;
